/**
 * @file   src\containers\Manager\Report\StaffSummary.tsx
 * @brief  Staff Summary Report page
 * @date   Oct, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { Row, Col, Table, Button, useState, moment, useEffect, Dropdown } from '../../../components/ThirdPartyComponents';
import Strings from '../../../assests/strings/Strings.json';
import PDFIcon from '../../../assests/icons/PDF';
import Sort from '../../../assests/icons/Sort';
import KKIDatepicker from '../../../components/KKIDatepicker';
import {
  DATE_END,
  DATE_FORMAT_MM_DD_YYYY_DDD,
  DATE_FORMAT_MM_DD_YYYY_FILTER,
  DATE_FORMAT_YYYY_MM_DD,
  DATE_START,
  HTTP_STATUS_200,
  MONTHS_LABEL,
  NUMBER_0,
  NUMBER_1,
  NUMBER_3,
  PAGE_SIZE,
} from '../../../utils/constants';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { SortOrderType, StaffSummaryReportSortField } from '../../../utils/enums';
import { MessageToaster } from '../../../utils/toastUtils';
import { RootState } from '../../../store';
import { getDateDifference, stringFormat } from '../../../utils/helper';
import { exportStaffSummaryReportAsExcel, exportStaffSummaryReportAsPdf, getStaffSummaryReport, revertAllReportState } from '../../../store/actions/reportAction';
import { IStaffSummaryPdfReportRequest, IStaffSummaryReportList, IStaffSummaryReportRequest } from '../../../interfaces/reportInterface';
import SortDownIcon from '../../../assests/icons/SortDown';
import SortUpIcon from '../../../assests/icons/SortUp';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Paginate';
import EXPPDF from '../../../assests/icons/PDFIcon';
import EXPExcel from '../../../assests/icons/Excel';
import { DATE_RANGE_VALIDATION_MESSAGE } from '../../../messages/validationMessages';

// Toast object creation.
const toast = new MessageToaster();
// Define item count to be displayed per page.
const pageItemCount: number = PAGE_SIZE;

const StaffSummary = () => {
  // Declare action dispatch.
  const dispatch = useAppDispatch();

  // Access redux state variables.
  const {
    isStaffSummaryReportLoading,
    isStaffSummaryReportSuccess,
    staffSummaryReportErrorCode,
    staffSummaryReportMessage,
    staffSummaryReportData,
    totalStaffSummary,
    overstaffCap,
    isExportStaffSummaryPdfLoading,
    isExportStaffSummaryPdfSuccess,
    exportStaffSummaryPdfErrorCode,
    exportStaffSummaryPdfMessage,
    staffSummaryPdfData,
    isExportStaffSummaryExcelLoading,
    isExportStaffSummaryExcelSuccess,
    exportStaffSummaryExcelErrorCode,
    exportStaffSummaryExcelMessage,
    staffSummaryExcelData,
  } = useAppSelector((state: RootState) => state.report);

  // Initialize component state variables.
  const [startDateSelected, setStartDateSelected] = useState<Date | null>(moment().toDate());
  const [endDateSelected, setEndDateSelected] = useState<Date | null>(moment().toDate());
  const [pageSize] = useState<number>(pageItemCount);
  const [pageIndex, setPageIndex] = useState<number>(NUMBER_1);
  const [sortBy, setSortBy] = useState<string>(StaffSummaryReportSortField[StaffSummaryReportSortField.shiftdate]);
  const [sortOrder, setSortOrder] = useState<number>(SortOrderType.ASC);

  // Method to fetch staff summary api data.
  const fetchStaffSummary = () => {
    let fromDate = '';
    let toDate = '';
    if (startDateSelected && endDateSelected) {
      fromDate = moment(new Date(startDateSelected)).format(DATE_FORMAT_YYYY_MM_DD);
      toDate = moment(new Date(endDateSelected)).format(DATE_FORMAT_YYYY_MM_DD);
    }
    const reportRequest: IStaffSummaryReportRequest = {
      fromDate,
      toDate,
      page: pageIndex,
      limit: pageSize,
      order: SortOrderType[sortOrder],
      sort: sortBy,
    };
    dispatch(getStaffSummaryReport(reportRequest));
  };

  // Handle component side effect and fetch staff summary data.
  useEffect(() => {
    fetchStaffSummary();
    return () => {
      dispatch(revertAllReportState());
    };
  }, [pageIndex, sortBy, sortOrder]);

  // Callout report fetch api response state change.
  useEffect(() => {
    if (!isStaffSummaryReportSuccess && staffSummaryReportErrorCode > HTTP_STATUS_200 && staffSummaryReportMessage) {
      toast.toastError(staffSummaryReportMessage);
    }
  }, [isStaffSummaryReportSuccess, staffSummaryReportErrorCode, staffSummaryReportMessage]);

  // Export pdf api response state change handling.
  useEffect(() => {
    if (isExportStaffSummaryPdfSuccess && staffSummaryPdfData) {
      const pdfBlob = new Blob([staffSummaryPdfData], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Create a link element, set download attributes, and trigger a click
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.setAttribute('download', 'StaffSummaryReport.pdf'); // Set the PDF filename
      document.body.appendChild(link);
      link.click();
      toast.toastSuccess(Strings.Text.StaffSummaryExportSuccessText);
      // Cleanup: remove the link and revoke the URL
      document.body.removeChild(link);
      URL.revokeObjectURL(pdfUrl);
      dispatch(revertAllReportState());
    } else if (!isExportStaffSummaryPdfSuccess && exportStaffSummaryPdfErrorCode > HTTP_STATUS_200 && exportStaffSummaryPdfMessage) {
      toast.toastError(exportStaffSummaryPdfMessage);
      dispatch(revertAllReportState());
    }
  }, [isExportStaffSummaryPdfSuccess, exportStaffSummaryPdfErrorCode, exportStaffSummaryPdfMessage, staffSummaryPdfData]);

  // Export excel api response state change handling.
  useEffect(() => {
    if (isExportStaffSummaryExcelSuccess && staffSummaryExcelData) {
      const excelBlob = new Blob([staffSummaryExcelData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const excelUrl = URL.createObjectURL(excelBlob);

      // Create a link element, set download attributes, and trigger a click
      const link = document.createElement('a');
      link.href = excelUrl;
      link.setAttribute('download', 'StaffSummaryReport.xlsx'); // Set the Excel filename
      document.body.appendChild(link);
      link.click();
      toast.toastSuccess(Strings.Text.StaffSummaryExportSuccessText);
      // Cleanup: remove the link and revoke the URL
      document.body.removeChild(link);
      URL.revokeObjectURL(excelUrl);
      dispatch(revertAllReportState());
    } else if (!isExportStaffSummaryExcelSuccess && exportStaffSummaryExcelErrorCode > HTTP_STATUS_200 && exportStaffSummaryExcelMessage) {
      toast.toastError(exportStaffSummaryExcelMessage);
      dispatch(revertAllReportState());
    }
  }, [isExportStaffSummaryExcelSuccess, exportStaffSummaryExcelErrorCode, exportStaffSummaryExcelMessage, staffSummaryExcelData]);

  // Start/end date input select event handler.
  const handleDateChange = (date: Date | null, type: string) => {
    let days = NUMBER_1;
    if (type === DATE_START) {
      days = getDateDifference(moment(date).format(DATE_FORMAT_YYYY_MM_DD), moment(endDateSelected).format(DATE_FORMAT_YYYY_MM_DD));
      if (days < 0) {
        setStartDateSelected(date);
        setEndDateSelected(date);
      } else {
        setStartDateSelected(date);
      }
    } else {
      days = getDateDifference(moment(startDateSelected).format(DATE_FORMAT_YYYY_MM_DD), moment(date).format(DATE_FORMAT_YYYY_MM_DD));
      setEndDateSelected(date);
    }
  };

  // Generate report button click handler.
  const onGenerateReport = () => {
    if (moment(endDateSelected).diff(moment(startDateSelected), MONTHS_LABEL, true) > NUMBER_3) {
      toast.toastError(DATE_RANGE_VALIDATION_MESSAGE);
      return;
    }
    setSortBy(StaffSummaryReportSortField[StaffSummaryReportSortField.shiftdate]);
    setSortOrder(SortOrderType.ASC);
    if (pageIndex !== NUMBER_1) {
      setPageIndex(NUMBER_1);
    } else {
      fetchStaffSummary();
    }
  };

  // Sort header change event.
  const handleSort = (field: string) => {
    let newSortOrder = SortOrderType.ASC;
    if (field === sortBy) {
      newSortOrder = sortOrder === SortOrderType.ASC ? SortOrderType.DESC : SortOrderType.ASC;
    }
    setSortBy(field);
    setSortOrder(newSortOrder);
  };

  // Export staff summary request binding.
  const getStaffSummaryReportExportRequest = (): IStaffSummaryPdfReportRequest => {
    let fromDate = '';
    let toDate = '';
    if (startDateSelected && endDateSelected) {
      fromDate = moment(new Date(startDateSelected)).format(DATE_FORMAT_YYYY_MM_DD);
      toDate = moment(new Date(endDateSelected)).format(DATE_FORMAT_YYYY_MM_DD);
    }
    return {
      fromDate,
      toDate,
      page: NUMBER_0,
      order: SortOrderType[sortOrder],
      sort: sortBy,
    };
  };

  // Handle the "Export as PDF" button click event.
  const handleExportPDFClick = (): void => {
    const exportRequest = getStaffSummaryReportExportRequest();
    dispatch(exportStaffSummaryReportAsPdf(exportRequest));
  };

  // Handle the "Export as Excel" button click event.
  const handleExportExcelClick = (): void => {
    const exportRequest = getStaffSummaryReportExportRequest();
    dispatch(exportStaffSummaryReportAsExcel(exportRequest));
  };

  // Set classname for summary columns.
  const setClassName = (summaryVal: number) => {
    let summaryClassName = '';
    if (summaryVal < NUMBER_0) {
      summaryClassName = 'staff-red';
    } else if (summaryVal > overstaffCap) {
      summaryClassName = 'staff-green';
    } else {
      summaryClassName = 'staff-yellow';
    }
    return summaryClassName;
  };

  return (
    <div>
      <div className="pageheader">
        <Row className="align-items-center justify-content-between">
          <Col>
            <h1>{Strings.HD.StaffSummaryReport}</h1>
          </Col>
        </Row>
      </div>
      <div className="filter-main mb-3">
        <Row className="d-flex justify-content-between">
          <Col xl={6} md={12} sm={12}>
            <Row className="align-items-center">
              <Col xs="auto" className="px-3">
                {Strings.Schedule.DateFrom}
              </Col>
              <Col xs className="calendar-mob-left">
                <KKIDatepicker
                  id="startDate"
                  name="startDate"
                  placeholder={Strings.Label.StartDate}
                  value={startDateSelected}
                  dateFormat={DATE_FORMAT_MM_DD_YYYY_FILTER}
                  onChangeDatepicker={(date: any) => handleDateChange(date, DATE_START)}
                />
              </Col>
              <Col xs="auto" className="px-0">
                {Strings.Schedule.DateFilterText}
              </Col>
              <Col xs className="calendar-mob-right">
                <KKIDatepicker
                  id="endDate"
                  name="endDate"
                  placeholder={Strings.Label.EndDate}
                  value={endDateSelected}
                  dateFormat={DATE_FORMAT_MM_DD_YYYY_FILTER}
                  minDate={startDateSelected || moment().toDate()}
                  onChangeDatepicker={(date: any) => handleDateChange(date, DATE_END)}
                />
              </Col>
              <Col md="auto" className="ps-0">
                <Button variant="primary" type="button" onClick={() => onGenerateReport()}>
                  {Strings.Button.GenerateReport}
                </Button>
              </Col>
            </Row>
          </Col>
          <Col md="auto">
            <Dropdown className="exportfile">
              <Dropdown.Toggle variant="outline-primary" id="dropdown-basic">
                <PDFIcon />
                {Strings.Button.Export}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item href={void 0} onClick={handleExportPDFClick}>
                  <EXPPDF />
                  {Strings.Button.PDF}
                </Dropdown.Item>
                <Dropdown.Item href={void 0} onClick={handleExportExcelClick}>
                  <EXPExcel />
                  {Strings.Button.Excel}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>
      </div>

      <div className="table-border">
        <Table striped bordered hover responsive className="staff-summary">
          <thead>
            <tr>
              <th onClick={() => handleSort(StaffSummaryReportSortField[StaffSummaryReportSortField.shiftdate])} className="w-300" style={{ cursor: 'pointer' }}>
                {Strings.Label.Date}
                <span className="ms-1">
                  {sortBy !== StaffSummaryReportSortField[StaffSummaryReportSortField.shiftdate] && <Sort />}
                  {sortBy === StaffSummaryReportSortField[StaffSummaryReportSortField.shiftdate] && sortOrder === SortOrderType.DESC && <SortDownIcon />}
                  {sortBy === StaffSummaryReportSortField[StaffSummaryReportSortField.shiftdate] && sortOrder === SortOrderType.ASC && <SortUpIcon />}
                </span>
              </th>
              <th onClick={() => handleSort(StaffSummaryReportSortField[StaffSummaryReportSortField.daysummary])} style={{ cursor: 'pointer' }}>
                {Strings.Label.DayStaffing}
                <span className="ms-1">
                  {sortBy !== StaffSummaryReportSortField[StaffSummaryReportSortField.daysummary] && <Sort />}
                  {sortBy === StaffSummaryReportSortField[StaffSummaryReportSortField.daysummary] && sortOrder === SortOrderType.DESC && <SortDownIcon />}
                  {sortBy === StaffSummaryReportSortField[StaffSummaryReportSortField.daysummary] && sortOrder === SortOrderType.ASC && <SortUpIcon />}
                </span>
              </th>
              <th onClick={() => handleSort(StaffSummaryReportSortField[StaffSummaryReportSortField.eveningsummary])} style={{ cursor: 'pointer' }}>
                {Strings.Label.EveningStaffing}
                <span className="ms-1">
                  {sortBy !== StaffSummaryReportSortField[StaffSummaryReportSortField.eveningsummary] && <Sort />}
                  {sortBy === StaffSummaryReportSortField[StaffSummaryReportSortField.eveningsummary] && sortOrder === SortOrderType.DESC && <SortDownIcon />}
                  {sortBy === StaffSummaryReportSortField[StaffSummaryReportSortField.eveningsummary] && sortOrder === SortOrderType.ASC && <SortUpIcon />}
                </span>
              </th>
              <th onClick={() => handleSort(StaffSummaryReportSortField[StaffSummaryReportSortField.nightsummary])}>
                {Strings.Label.NightStaffing}
                <span className="ms-1">
                  {sortBy !== StaffSummaryReportSortField[StaffSummaryReportSortField.nightsummary] && <Sort />}
                  {sortBy === StaffSummaryReportSortField[StaffSummaryReportSortField.nightsummary] && sortOrder === SortOrderType.DESC && <SortDownIcon />}
                  {sortBy === StaffSummaryReportSortField[StaffSummaryReportSortField.nightsummary] && sortOrder === SortOrderType.ASC && <SortUpIcon />}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {staffSummaryReportData &&
              staffSummaryReportData.length > NUMBER_0 &&
              staffSummaryReportData.map((att: IStaffSummaryReportList, index: number) => (
                <tr key={`${att.shiftDate}-${index}`}>
                  <td>{moment.utc(att.shiftDate).format(DATE_FORMAT_MM_DD_YYYY_DDD)}</td>
                  <td className={setClassName(att.daySummary)}>{att.daySummary}</td>
                  <td className={setClassName(att.eveningSummary)}>{att.eveningSummary}</td>
                  <td className={setClassName(att.nightSummary)}>{att.nightSummary}</td>
                </tr>
              ))}
            {!staffSummaryReportData ||
              (staffSummaryReportData && staffSummaryReportData.length === NUMBER_0 && (
                <tr>
                  <td colSpan={4} className="text-center">
                    {Strings.Text.NoDataFound}
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </div>
      <div className="d-flex justify-content-between mt-4">
        <span>
          {(() => {
            const displayText = '';
            if (staffSummaryReportData && staffSummaryReportData.length > NUMBER_0 && totalStaffSummary > 0) {
              const callOut = totalStaffSummary === NUMBER_1 ? NUMBER_1 : pageIndex;
              const start = (callOut - NUMBER_1) * pageSize + NUMBER_1;
              const end = (callOut - NUMBER_1) * pageSize + staffSummaryReportData.length;
              return stringFormat(Strings.Text.StaffSummaryReportPaginationText, start, end, totalStaffSummary);
            }
            return displayText;
          })()}
        </span>
        {staffSummaryReportData && staffSummaryReportData.length > NUMBER_0 && totalStaffSummary > pageSize && (
          <Pagination totalCount={totalStaffSummary} pageLimit={pageSize} setCurrentPage={(page: number) => setPageIndex(page)} currentPage={pageIndex - NUMBER_1} prevPage={-1} />
        )}
      </div>
      {(isStaffSummaryReportLoading || isExportStaffSummaryPdfLoading || isExportStaffSummaryExcelLoading) && <Loader />}
    </div>
  );
};
export default StaffSummary;
